// Pure helpers for image loading priority (no React). The provider and hook live in
// @core/helpers/client/contexts/ImagePriorityContext, which re-exports everything here.
//
// Levels:
//   "lcp"     preload + fetchpriority=high + eager: the one image that is the page's LCP (hero / first gallery image)
//   "preload" preload + eager: images in the first viewport (first widgets, first row of cards)
//   "eager"   loads immediately at low priority, no preload: above the fold but not worth bandwidth before the hero (logo,
//             banner, thumbnails). fetchPriority="low" is what keeps it out of the preloads: React 19 adds a
//             <link rel="preload"> for every non-lazy <img> in the server HTML unless it is lazy or low priority.
//   "lazy"    loads when it nears the viewport

export const IMAGE_PRIORITY_LEVELS = ["lcp", "preload", "eager", "lazy"];

/** true → "preload", false → "lazy", a level name → itself, anything else → undefined. */
export const normalize_image_priority = (value) => {
	if (value === true) return "preload";
	if (value === false) return "lazy";
	return IMAGE_PRIORITY_LEVELS.includes(value) ? value : undefined;
};

/** The level for a widget at position `index` in a page's widget list: the first `first` are "preload", the rest "lazy". */
export const widget_priority = (index, first = 3) => (Number(index) < first ? "preload" : "lazy");

/**
 * The level for slide `index` of a slider: the first slide of a hero slider is the LCP image ("lcp"); the other slides
 * that are visible at once (`visible`) keep the slider's level; hidden slides are "lazy".
 */
export const slide_priority = (index, { visible = 1, hero = false, level = "preload" } = {}) => {
	if (index === 0 && hero) return "lcp";
	return index < visible ? normalize_image_priority(level) || "lazy" : "lazy";
};

/**
 * The level that plain next/image props ask for, so a caller passing `priority`, `preload`, `loading` or
 * `fetchPriority` straight to a core component keeps what it asked for (and never ends up with priority + lazy, which
 * next/image throws on). Nothing passed → undefined.
 */
export const level_from_next_props = ({ priority, preload, loading, fetchPriority } = {}) => {
	if (priority || preload) return fetchPriority === "high" ? "lcp" : "preload";
	if (fetchPriority === "high") return "lcp";
	if (loading === "eager") return "eager";
	if (loading === "lazy") return "lazy";
	return undefined;
};

/**
 * Final level for one image: `hidden` (the image is in a copy of a block the CSS hides on this device → "lazy", over
 * everything else), then an explicit `priority_level`, then next/image props the caller passed (`next_props`),
 * then the legacy `isLCP` (true → "lcp", false → "lazy"), then the nearest provider (`context`), then the component's
 * old default (`fallback`): "lcp" for ImageComponent / ImageServerComponent (isLCP defaulted to true), "lazy" for
 * MediaComponent (its main image never got priority).
 */
export const resolve_image_priority = ({ hidden, priority_level, next_props, isLCP, context, fallback = "lcp" } = {}) => {
	if (hidden) return "lazy";
	const explicit = normalize_image_priority(priority_level) || level_from_next_props(next_props);
	if (explicit) return explicit;
	if (isLCP === true) return "lcp";
	if (isLCP === false) return "lazy";
	return normalize_image_priority(context) || fallback;
};

/** next/image props for a level. `priority` (not Next 16's `preload`) so the core harness on Next 15 works too. */
export const image_priority_props = (level) => {
	switch (level) {
		case "lcp":
			return { priority: true, fetchPriority: "high", loading: "eager" };
		case "preload":
			return { priority: true, loading: "eager" };
		case "eager":
			return { priority: false, loading: "eager", fetchPriority: "low" };
		default:
			return { priority: false, loading: "lazy" };
	}
};

/** Images loaded in the first viewport show no loading skeleton: the visitor sees the image, not a placeholder. */
export const shows_skeleton = (level) => level === "lazy";

/**
 * How many slides a Swiper config can show at once at its widest breakpoint (slidesPerView × grid rows). Slides past
 * this count are hidden on first paint, so their images stay lazy even inside a "preload" widget. `auto` slides count
 * as `auto_count`.
 */
export const visible_slide_count = ({ slidesPerView, breakpoints, grid } = {}, auto_count = 4) => {
	const per_view = (value) => Math.ceil((value === "auto" ? auto_count : Number(value)) || 1);
	const rows = (g) => Math.max(1, Number(g?.rows) || 1);
	let count = per_view(slidesPerView ?? 1) * rows(grid);
	for (const bp of Object.values(breakpoints || {})) {
		count = Math.max(count, per_view(bp?.slidesPerView ?? slidesPerView ?? 1) * rows(bp?.grid ?? grid));
	}
	return count;
};
